import { Fragment, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import dayjs from 'dayjs'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  Tabs,
  Tab,
  DialogTitle,
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { SaveButton } from '@ui/actions'
import { StateIndicator } from '@ui/feedback'
import { MinimizeTabsWrapper } from '@ui/navigation'
import { useSnackbar } from '@ui/snackbar'
import { roofStoreyKey } from '@editorStores'
import { useGetPlanSettings, useModelStoreys, useDebouncedPlanSettingsSave } from '@editorHooks'
import { todayStringDE } from '@utils'
import {
  postCreateLoadPlan,
  postCreatePositionPlan,
  postCreateStiffeningPlan,
  savePlan,
} from '@mutations'
import { buildErrorMessage } from 'src/constants'
import ExportSettingsForm from '../../../Export/components/ExportSettingsForm'
import AddPlanItem from './components/AddPlanItem'
import CombinedPositionPlanItem from './components/CombinedPositionPlanItem'
import SingleStoreyPositionPlanItem from './components/SingleStoreyPositionPlanItem'
import UploadNewPlan from './components/UploadNewPlan'
import { DefaultValues } from './components/UploadNewPlan/types'

interface Props {
  open: boolean
  onClose: () => void
  onAddPlan: (plan: Plan) => void
}

const AddPlanDialog = ({ open, onClose, onAddPlan }: Props) => {
  const [tab, setTab] = useState<'generate' | 'upload'>('generate')
  const [planDate, setPlanDate] = useState(todayStringDE())

  const storeys = useModelStoreys()

  const handInDateToRequestParam = (handInDate: string) => {
    const dayjsHandInDate = dayjs(handInDate, 'DD.MM.YYYY')
    return dayjsHandInDate.format('DD-MM-YYYY')
  }

  const { projectId } = useParams()

  const { enqueueSnackbar } = useSnackbar()

  const { mutate: addPlanElementMutation, isLoading } = useMutation(
    async ({ file, title, comment }: DefaultValues) => {
      const plan: Plan = await savePlan.request(projectId as string, {
        file,
        title,
        comment,
      })

      return { plan }
    },
    {
      onSuccess: async ({ plan }) => {
        onAddPlan(plan)
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Hinzufügen des Plans'), {
          variant: 'error',
        })
      },
    },
  )

  const { data: planSettings } = useGetPlanSettings()
  const { onPlanSettingsChange, flushPlanSettings, stateIndicatorType } =
    useDebouncedPlanSettingsSave()

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        <MinimizeTabsWrapper>
          <Stack direction="row" justifyContent="space-between">
            <Tabs value={tab} onChange={(_, v) => setTab(v as 'generate' | 'upload')}>
              <Tab label="Plan generieren" value="generate" />
              <Tab label="Hochladen" value="upload" />
            </Tabs>
            {tab === 'generate' && (
              <Stack direction="row" spacing={1} justifyContent="flex-end" p={1}>
                <StateIndicator type={stateIndicatorType} onSave={flushPlanSettings} />
              </Stack>
            )}
          </Stack>
        </MinimizeTabsWrapper>
      </DialogTitle>
      <DialogContent dividers>
        {tab === 'generate' && !!planSettings && (
          <Stack direction="column" spacing={1}>
            <Box>
              <ExportSettingsForm
                planSettings={planSettings}
                onPlanSettingsChange={onPlanSettingsChange}
                setPlanDate={setPlanDate}
                planDate={planDate}
              />
            </Box>
            <Divider>Positionspläne (PP) - PDF</Divider>
            <Box>
              {storeys.map(storey => {
                if (storey === roofStoreyKey) return <></>
                return (
                  <Fragment key={storey}>
                    <SingleStoreyPositionPlanItem
                      storey={storey}
                      request={(
                        projectId: string,
                        storey: string,
                        params: Record<string, string | number | boolean>,
                      ) => postCreatePositionPlan.request(projectId, [storey], params)}
                      params={{
                        paper_format: planSettings.paperFormat,
                        orientation: planSettings.orientation,
                        scale: planSettings.scale,
                        add_header: true,
                        f_format: 'pdf',
                        hand_in_date: handInDateToRequestParam(planDate),
                      }}
                    />
                  </Fragment>
                )
              })}
              <CombinedPositionPlanItem
                params={{
                  paper_format: planSettings.paperFormat,
                  orientation: planSettings.orientation,
                  scale: planSettings.scale,
                  add_header: true,
                  f_format: 'pdf',
                  hand_in_date: handInDateToRequestParam(planDate),
                }}
              />
            </Box>
            <Divider>Aussteifungspläne (AP) - PDF</Divider>

            <Stack direction="column" spacing={1}>
              <Box>
                {storeys.map(storey => {
                  if (storey === roofStoreyKey) return <></>
                  return (
                    <Fragment key={storey}>
                      <SingleStoreyPositionPlanItem
                        storey={storey}
                        request={(
                          projectId: string,
                          storey: string,
                          params: Record<string, string | number | boolean>,
                        ) => {
                          const newParams = {
                            ...params,
                            storey: storey,
                          }
                          return postCreateStiffeningPlan.request(projectId, newParams)
                        }}
                        params={{
                          paper_format: planSettings.paperFormat,
                          orientation: planSettings.orientation,
                          scale: planSettings.scale,
                          add_header: true,
                          f_format: 'pdf',
                          hand_in_date: handInDateToRequestParam(planDate),
                        }}
                      />
                    </Fragment>
                  )
                })}
              </Box>
            </Stack>

            <Divider />

            <AddPlanItem
              title="Lastenplan"
              request={(projectId: string, params: Record<string, string | number | boolean>) =>
                postCreateLoadPlan.request(projectId, params)
              }
              params={{
                paper_format: planSettings.paperFormat,
                orientation: planSettings.orientation,
                scale: planSettings.scale,
                add_header: true,
                f_format: 'pdf',
                hand_in_date: handInDateToRequestParam(planDate),
              }}
            />
          </Stack>
        )}
        {tab === 'upload' && (
          <Box minWidth={500}>
            <UploadNewPlan onSubmit={data => addPlanElementMutation(data)} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {tab === 'upload' && (
          <SaveButton loading={isLoading} form="upload-plan-form" type="submit">
            Speichern
          </SaveButton>
        )}
        <Button variant="text" color="primary" onClick={onClose}>
          Schließen
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AddPlanDialog
